import { useCallback } from "react";
import {
  createCurrentMeasurementsInDatabase,
  createMeasurementsInDatabase,
  createTargetMeasurementsInDatabase,
  updateCurrentMeasurementsInDatabase,
  updateMeasurementsInDatabase,
  updateTargetMeasurementsInDatabase,
} from "../services/MeasurementsService";

const useMeasurementsActions = () => {
  const createMeasurements = useCallback(async (instanceId, measurements) => {
    try {
      if (!instanceId) {
        return;
      }

      if (
        !measurements ||
        !Array.isArray(measurements) ||
        measurements.length === 0
      ) {
        return;
      }

      const payload = {
        instanceId,
        measurements,
      };

      const result = await createMeasurementsInDatabase(payload);
      // Parse the comma-separated measurement IDs
      const createdIds = result ? result.split(",").filter(id => id) : [];
      return createdIds;
    } catch (error) {
      throw error;
    }
  }, []);

  const updateMeasurements = useCallback(async (instanceId, measurements) => {
    try {
      if (!instanceId) {
        return;
      }

      if (
        !measurements ||
        !Array.isArray(measurements) ||
        measurements.length === 0
      ) {
        return;
      }

      const payload = {
        instanceId,
        measurements,
      };

      const result = await updateMeasurementsInDatabase(payload);
      return result;
    } catch (error) {
      throw error;
    }
  }, []);

  const syncMeasurementsWithInstances = useCallback(
    async (instances, measurementData) => {
      const syncResult = {
        createdIds: [],
        updated: false,
      };

      try {
        if (!instances || Object.keys(instances).length === 0) {
          return syncResult;
        }

        if (!measurementData) {
          return syncResult;
        }

        // Process lateral measurements
        if (
          measurementData.lateral?.annotations &&
          instances.sagittal_dx_instance_id
        ) {
          const lateralMeasurements = Object.values(
            measurementData.lateral.annotations
          )
            .filter(
              annotation => annotation.representation?.value !== undefined
            )
            .map(annotation => ({
              unique_identifier: annotation.unique_identifier,
              value: annotation.representation.value,
            }));

          if (lateralMeasurements.length > 0) {
            const instanceId = instances.sagittal_dx_instance_id._id;
            const hasExistingMeasurements =
              instances.sagittal_dx_instance_id.measurements?.length > 0;

            if (hasExistingMeasurements) {
              await updateMeasurements(instanceId, lateralMeasurements);
              syncResult.updated = true;
            } else {
              const createdIds = await createMeasurements(
                instanceId,
                lateralMeasurements
              );
              if (createdIds?.length) {
                syncResult.createdIds.push(...createdIds);
              }
            }
          }
        }

        // Process frontal measurements
        if (
          measurementData.frontal?.annotations &&
          instances.coronal_dx_instance_id
        ) {
          const frontalMeasurements = Object.values(
            measurementData.frontal.annotations
          )
            .filter(
              annotation => annotation.representation?.value !== undefined
            )
            .map(annotation => ({
              unique_identifier: annotation.unique_identifier,
              value: annotation.representation.value,
            }));

          if (frontalMeasurements.length > 0) {
            const instanceId = instances.coronal_dx_instance_id._id;
            const hasExistingMeasurements =
              instances.coronal_dx_instance_id.measurements?.length > 0;

            if (hasExistingMeasurements) {
              await updateMeasurements(instanceId, frontalMeasurements);
              syncResult.updated = true;
            } else {
              const createdIds = await createMeasurements(
                instanceId,
                frontalMeasurements
              );
              if (createdIds?.length) {
                syncResult.createdIds.push(...createdIds);
              }
            }
          }
        }
      } catch (error) {}

      return syncResult;
    },
    [createMeasurements, updateMeasurements]
  );

  // Plan-based measurement functions for Align module
  const createCurrentMeasurements = useCallback(
    async (planId, measurements) => {
      try {
        if (!planId) {
          return;
        }

        if (
          !measurements ||
          !Array.isArray(measurements) ||
          measurements.length === 0
        ) {
          return;
        }

        const payload = {
          digitalTwinPlanId: planId,
          measurements,
        };

        const result = await createCurrentMeasurementsInDatabase(payload);
        const createdIds = result ? result.split(",").filter(id => id) : [];
        return createdIds;
      } catch (error) {
        throw error;
      }
    },
    []
  );

  const updateCurrentMeasurements = useCallback(
    async (planId, measurements) => {
      try {
        if (!planId) {
          return;
        }

        if (
          !measurements ||
          !Array.isArray(measurements) ||
          measurements.length === 0
        ) {
          return;
        }

        const payload = {
          digitalTwinPlanId: planId,
          measurements,
        };

        const result = await updateCurrentMeasurementsInDatabase(payload);
        return result;
      } catch (error) {
        throw error;
      }
    },
    []
  );

  const createTargetMeasurements = useCallback(async (planId, measurements) => {
    try {
      if (!planId) {
        return;
      }

      if (
        !measurements ||
        !Array.isArray(measurements) ||
        measurements.length === 0
      ) {
        return;
      }

      const payload = {
        digitalTwinPlanId: planId,
        measurements,
      };

      const result = await createTargetMeasurementsInDatabase(payload);
      const createdIds = result ? result.split(",").filter(id => id) : [];
      return createdIds;
    } catch (error) {
      throw error;
    }
  }, []);

  const updateTargetMeasurements = useCallback(async (planId, measurements) => {
    try {
      if (!planId) {
        return;
      }

      if (
        !measurements ||
        !Array.isArray(measurements) ||
        measurements.length === 0
      ) {
        return;
      }

      const payload = {
        digitalTwinPlanId: planId,
        measurements,
      };

      const result = await updateTargetMeasurementsInDatabase(payload);
      return result;
    } catch (error) {
      throw error;
    }
  }, []);

  const syncMeasurementsWithPlan = useCallback(
    async (planId, plan, measurementData, measurementType) => {
      const syncResult = {
        createdIds: [],
        updated: false,
      };

      try {
        if (!planId) {
          return syncResult;
        }

        if (!measurementData) {
          return syncResult;
        }

        const sampleLateral = measurementData.lateral?.annotations
          ? Object.values(measurementData.lateral.annotations)[0]
          : null;
        const sampleFrontal = measurementData.frontal?.annotations
          ? Object.values(measurementData.frontal.annotations)[0]
          : null;

        const findMarker = data => {
          if (!data?.lateral?.annotations && !data?.frontal?.annotations) {
            return null;
          }
          const fromLateral = data.lateral?.annotations
            ? Object.values(data.lateral.annotations).find(
                entry => entry?.unique_identifier === 14014
              )
            : null;
          const fromFrontal =
            !fromLateral && data.frontal?.annotations
              ? Object.values(data.frontal.annotations).find(
                  entry => entry?.unique_identifier === 14014
                )
              : null;
          const picked = fromLateral || fromFrontal;
          return picked
            ? {
                unique_identifier: picked.unique_identifier,
                value: picked.representation?.value,
                source: fromLateral ? "lateral" : "frontal",
              }
            : null;
        };

        // Determine which functions to use based on measurement type
        const isCurrentMeasurement = measurementType === "current";
        const createFn = isCurrentMeasurement
          ? createCurrentMeasurements
          : createTargetMeasurements;
        const updateFn = isCurrentMeasurement
          ? updateCurrentMeasurements
          : updateTargetMeasurements;

        // Process lateral measurements
        if (measurementData.lateral?.annotations) {
          const lateralMeasurements = Object.values(
            measurementData.lateral.annotations
          )
            .filter(
              annotation => annotation.representation?.value !== undefined
            )
            .map(annotation => ({
              unique_identifier: annotation.unique_identifier,
              value: annotation.representation.value,
            }));

          if (lateralMeasurements.length > 0) {
            const measurementField = isCurrentMeasurement
              ? "current_measurements"
              : "target_measurements";
            const hasExistingMeasurements =
              plan?.[measurementField]?.length > 0;

            if (hasExistingMeasurements) {
              await updateFn(planId, lateralMeasurements);
              syncResult.updated = true;
            } else {
              const createdIds = await createFn(planId, lateralMeasurements);
              if (createdIds?.length) {
                syncResult.createdIds.push(...createdIds);
              }
            }
          }
        }

        // Process frontal measurements
        if (measurementData.frontal?.annotations) {
          const frontalMeasurements = Object.values(
            measurementData.frontal.annotations
          )
            .filter(
              annotation => annotation.representation?.value !== undefined
            )
            .map(annotation => ({
              unique_identifier: annotation.unique_identifier,
              value: annotation.representation.value,
            }));

          if (frontalMeasurements.length > 0) {
            const measurementField = isCurrentMeasurement
              ? "current_measurements"
              : "target_measurements";
            const hasExistingMeasurements =
              plan?.[measurementField]?.length > 0;

            if (hasExistingMeasurements) {
              await updateFn(planId, frontalMeasurements);
              syncResult.updated = true;
            } else {
              const createdIds = await createFn(planId, frontalMeasurements);
              if (createdIds?.length) {
                syncResult.createdIds.push(...createdIds);
              }
            }
          }
        }
      } catch (error) {}

      return syncResult;
    },
    [
      createCurrentMeasurements,
      updateCurrentMeasurements,
      createTargetMeasurements,
      updateTargetMeasurements,
    ]
  );

  return {
    // Instance-based measurement functions (Check module)
    createMeasurements,
    updateMeasurements,
    syncMeasurementsWithInstances,
    // Plan-based measurement functions (Align module)
    createCurrentMeasurements,
    updateCurrentMeasurements,
    createTargetMeasurements,
    updateTargetMeasurements,
    syncMeasurementsWithPlan,
  };
};

export default useMeasurementsActions;
